import { rand } from '~/utils/random'
import type { ElementKey, Stats } from '~/types/game'
import { ELEMENTS } from '~/types/game'

/**
 * Boss skills (gamedesign.md §18): every act boss rolls ONE skill at map
 * generation — fully random for now — previewable in the boss intro, the
 * draft lineup and the node card. Mechanics live in utils/battle.ts and
 * useEncounter so the simulations share them.
 */
export type BossSkillId =
  | 'knockout'
  | 'eclipse'
  | 'fracture'
  | 'corrupted'
  | 'cursed'
  | 'unstable'
  | 'feint'
  | 'yinyang'
  | 'scientist'
  | 'bloodprice'
  | 'curse'
  | 'golem'

export interface BossSkillDef {
  id: BossSkillId
  name: string
  /** Short line for boss cards / previews. */
  blurb: string
}

/**
 * These aren't abstract "skills" — each is a BOSS with a name (per Javi,
 * v0.51). `name` is the boss's identity (it becomes the boss mission's
 * name at map generation); `blurb` describes how the fight works.
 */
export const BOSS_SKILLS: Record<BossSkillId, BossSkillDef> = {
  knockout: {
    id: 'knockout',
    name: 'The Sandman',
    blurb: 'Lulls a random ally to sleep each attempt — they can\'t join that fight. Quick bouts, and he never leaves the field: fail and he simply picks DIFFERENT sleepers. The lullaby deepens every other act — two sleepers from Act 3, up to four deep in the Gauntlet — but never more than your team can spare: at least three allies always stay awake (a team of three keeps two).',
  },
  eclipse: {
    id: 'eclipse',
    name: 'Umbrax',
    blurb: 'Devours one element at a time — your creatures add nothing to it. The eclipse rotates while you plan.',
  },
  fracture: {
    id: 'fracture',
    name: 'Myriad',
    blurb: 'Shatters when beaten: fragments split down to single-element Cores, each on its own timer. Destroy every Core to win.',
  },
  corrupted: {
    id: 'corrupted',
    name: 'Vessk',
    blurb: 'One element is blighted: every point committed over its requirement bites back with 5 extra damage on a failure.',
  },
  cursed: {
    id: 'cursed',
    name: 'Hexa',
    blurb: 'Curses the first slot: that creature loses its strongest relevant element for the fight. The curse deepens every other act — two slots from Act 3, up to four deep in the Gauntlet — but never more than your team can spare: at least three allies are always safe from it (a team of three keeps two).',
  },
  unstable: {
    id: 'unstable',
    name: 'Gorge',
    blurb: 'One requirement keeps swelling — even while you\'re busy elsewhere — and the growing element rotates. Strike fast.',
  },
  feint: {
    id: 'feint',
    name: 'Janus',
    blurb: 'One requirement is a feint: the moment you commit, it scatters onto the other elements.',
  },
  yinyang: {
    id: 'yinyang',
    name: 'Yin & Yang',
    blurb: 'Splits into two timed halves — and any overkill you commit against one strengthens its twin. Balance your forces.',
  },
  scientist: {
    id: 'scientist',
    name: 'Dr. Vex',
    blurb: 'The Amalgam is the target — a marathon of a fight that ties your team up for ages — while Dr. Vex meddles RELENTLESSLY with quick side jobs. Ignore him and he chips your HP.',
  },
  bloodprice: {
    id: 'bloodprice',
    name: 'Sanguis',
    blurb: 'Grossly overgrown — but it accepts blood: pay 4 HP to shave 3 points off any of its requirements, as often as you can afford.',
  },
  curse: {
    id: 'curse',
    name: 'Faust',
    blurb: 'Before you can fight, choose one of three curses. Every deal makes the fight worse — pick your poison.',
  },
  golem: {
    id: 'golem',
    name: 'Basalt',
    blurb: 'A towering colossus ringed by FIVE crystals — smash one to drain −2 from its strongest element, and a fresh crystal reforms seconds later. But Basalt runs a long fuse: fell it before the clock lapses or it reforms at full strength.',
  },
}

export const BOSS_SKILL_IDS = Object.keys(BOSS_SKILLS) as BossSkillId[]

export function rollBossSkill(): BossSkillId {
  return BOSS_SKILL_IDS[Math.floor(rand() * BOSS_SKILL_IDS.length)]!
}

/** Elements a boss statline actually requires. */
function requiredElements(required: Stats): ElementKey[] {
  return ELEMENTS.filter(e => required[e] > 0)
}

/**
 * Applies a rolled skill to a pre-rolled boss statline, in place. Called
 * at map generation (and by save migration for older maps):
 * - eclipse guarantees a dump stat (one requirement well below the peak),
 *   so there's always a "least painful" element to have blocked;
 * - fracture halves the statline (the three waves roughly add back up to
 *   ~1.35× a normal boss with 90% inheritance);
 * - corrupted / feint pre-roll their target element so it's previewable.
 */
export function applyBossSkill(
  boss: { required: Stats; damage: number; name?: string; skill?: string; skillElement?: ElementKey },
  skill: BossSkillId,
) {
  boss.skill = skill
  // The skill IS the boss: its identity replaces the random name.
  boss.name = BOSS_SKILLS[skill].name
  const elements = requiredElements(boss.required)
  if (skill === 'eclipse' && elements.length >= 2) {
    const peak = Math.max(...elements.map(e => boss.required[e]))
    const dump = elements.reduce((a, b) => (boss.required[b] < boss.required[a] ? b : a))
    const target = Math.max(1, Math.round(peak * 0.2))
    const freed = boss.required[dump] - target
    boss.required[dump] = target
    // Freed points land on the other requirements so the total holds.
    const rest = elements.filter(e => e !== dump)
    for (let i = 0; i < freed; i++) {
      boss.required[rest[Math.floor(rand() * rest.length)]!]++
    }
    boss.skillElement = dump
  }
  if (skill === 'fracture') {
    // 70% since v0.51 (50% → 60% → 70%) — with 90% inheritance the waves
    // total ~1.9× a normal boss; splits also expire now, so the pressure
    // is real.
    for (const element of elements) {
      boss.required[element] = Math.max(1, Math.round(boss.required[element] * 0.7))
    }
  }
  if (skill === 'corrupted') {
    boss.skillElement = elements[Math.floor(rand() * elements.length)]
  }
  if (skill === 'feint') {
    // Prefer a meaty requirement — scattering 1 point is no feint at all.
    const meaty = elements.filter(e => boss.required[e] >= 2)
    const pool = meaty.length > 0 ? meaty : elements
    boss.skillElement = pool[Math.floor(rand() * pool.length)]
  }
  if (skill === 'yinyang') {
    // Yin & Yang needs exactly FOUR required elements to split 2/2.
    let current = requiredElements(boss.required)
    while (current.length > 4) {
      // Merge the smallest requirement into the largest.
      const smallest = current.reduce((a, b) => (boss.required[b] < boss.required[a] ? b : a))
      const largest = current.reduce((a, b) => (boss.required[b] > boss.required[a] ? b : a))
      boss.required[largest] += boss.required[smallest]
      boss.required[smallest] = 0
      current = requiredElements(boss.required)
    }
    while (current.length < 4) {
      // Carve a new requirement out of the largest one.
      const largest = current.reduce((a, b) => (boss.required[b] > boss.required[a] ? b : a))
      const fresh = ELEMENTS.find(e => boss.required[e] === 0)
      if (!fresh || boss.required[largest] < 2) break
      const moved = Math.max(1, Math.floor(boss.required[largest] * 0.35))
      boss.required[largest] -= moved
      boss.required[fresh] = moved
      current = requiredElements(boss.required)
    }
  }
  if (skill === 'bloodprice') {
    // Sanguis rolls grossly overgrown; blood purchases trim it back.
    for (const element of requiredElements(boss.required)) {
      boss.required[element] = Math.max(1, Math.round(boss.required[element] * 1.4))
    }
  }
  if (skill === 'golem') {
    // Basalt is a colossus (buffed v0.61 — the five-crystal ring gives
    // far more chances to cut it down before its clock lapses).
    for (const element of requiredElements(boss.required)) {
      boss.required[element] = Math.max(1, Math.round(boss.required[element] * 1.5))
    }
  }
}
